import { AddOns, LayerIndex, SidePanelLayerType } from "@nova/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"

import { InfraredFlight } from "@nova/protobuf"

import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import { MapPortalContext } from "features/map/layers/MapPortalContext"
import { HotspotPluginActionBody } from "./plugin-actions/HotspotPluginActionBody"
import { ModalNames } from "features/modals/types"
import { HotspotPanel } from "panels/hotspot/HotspotPanel"
import { HotspotPreviewModal } from "panels/hotspot/HotspotPreviewModal"
import { HotspotTopBar } from "panels/hotspot/HotspotTopBar"
import { selectHotspotPanelIsOpen, selectHotspotPreviewModalIsOpen } from "panels/hotspot/selectors"
import { useContext, useEffect } from "react"
import { useLeftPanelRegistry } from "panels/routing/LeftPanelContext"
import { useTopBarRegistry } from "panels/routing/TopBarContext"

import { selectHotspotBoundingBoxes } from "./InfraredImageSlice"
import { PluginRegistry } from "plugins/plugin-registry"
import * as Icons from "@nova/component-icons"

import { zoomToHotspots } from "features/map/inspector-panel/hotspots/module"
import { getHotspotInspectorPanel } from "features/map/inspector-panel/hotspots/InspectorPanelHotspots"
import { selectUnmergedInfraredFlights } from "features/entity-caching/mergedFlights/selectors"
import {
    getGenericActiveFeatureSidePanelComponent,
    getGenericMultistepUploadFlowActiveFeatureSidePanelComponent,
    getGenericUploadFlowActiveFeatureSidePanelComponent,
} from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { HotspotMultifileUpload } from "./InfraredImageMultifileUpload"
import { useInfraredStatusAndProgress } from "panels/content/side-panel/progress/Hotspots"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import { getBadgeComponent } from "panels/content/side-panel/generics/GenericLayerListItemProgress"

import {
    updateInfraredFlight,
    deleteInfraredFlight,
} from "features/pubsub/entityActions/flights/infrared"
import { useIROrthomosaicLayer } from "./layers/orthomosaic"
import { useIROverlapLayer } from "./layers/overlap"

import { useIRScanAreaLayer } from "./layers/scanArea"
import { useIRImageOverlayLayer } from "./layers/imageOverlay"
import { useRemoveHotspotLayer } from "./layers/removeHotspot"
import { useDeletedHotspotsLayer } from "./layers/deletedHotspots"
import { InfraredImageAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/InfraredImageAssetsThumbnail"
import { closeHotspotPanel } from "panels/hotspot/slice"

export function registerInfraredImagePlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "infrared",
            displayName: "Hotspots",
            sidePanelLayerType: SidePanelLayerType.HotspotFlight,
            PrimaryIcon: Icons.HotspotPoint,
            ImageIconUrl: "/images/plugins/layers/hotspot.svg",
            updateAction: updateInfraredFlight,
            deleteAction: deleteInfraredFlight,
            confirmDelete: () => true,
            protobufMessage: InfraredFlight,
            selectAllEntities: selectUnmergedInfraredFlights,
            selectSingleEntity: selectInfraredFlightById,
            useUploadStatusAndProgress: useInfraredStatusAndProgress,
            zoomTo: zoomToHotspots,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                RightSlotComponent: getBadgeComponent({
                    useStatusAndProgress: useInfraredStatusAndProgress,
                }),
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: InfraredImageAssetsThumbnail,
            }),
        }))
        .implement("side-panel-active-feature-plugin", (spec) => ({
            SidePanelComponent: getGenericMultistepUploadFlowActiveFeatureSidePanelComponent({
                InitialRenderComponent: getGenericActiveFeatureSidePanelComponent({
                    spec,
                    RenderComponent: HotspotPluginActionBody,
                }),
                UploadFlowRenderComponent: getGenericUploadFlowActiveFeatureSidePanelComponent({
                    MultifileUploadComponent: HotspotMultifileUpload,
                }),
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getHotspotInspectorPanel(spec),
        }))
        .implement("bounding-box-data-provider", () => ({
            useBoundingBoxData: () => useAppSelector(selectHotspotBoundingBoxes),
            showLabel: true,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useIROrthomosaicLayer,
            zIndex: LayerIndex.Ortho,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useIROverlapLayer,
            zIndex: LayerIndex.OrthoOverlap,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useIRScanAreaLayer,
            zIndex: LayerIndex.Ortho,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useIRImageOverlayLayer,
            zIndex: LayerIndex.IRImage,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useRemoveHotspotLayer,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useDeletedHotspotsLayer,
        }))
        .implement("ui-controller", () => ({
            Controller: () => {
                const isPreviewModalOpen = useAppSelector(selectHotspotPreviewModalIsOpen)
                const isHotspotPanelOpen = useAppSelector(selectHotspotPanelIsOpen)

                const dispatch = useAppDispatch()
                const { register, unregister } = useContext(MapPortalContext)
                const { register: registerLeftPanel, unregister: unregisterLeftPanel } =
                    useLeftPanelRegistry()
                const { register: registerTopBar, unregister: unregisterTopBar } =
                    useTopBarRegistry()

                useEffect(() => {
                    if (isPreviewModalOpen) {
                        register("hotspot-image-preview-modal", HotspotPreviewModal)
                    } else {
                        unregister("hotspot-image-preview-modal")
                    }

                    if (isHotspotPanelOpen) {
                        registerLeftPanel({
                            key: "hotspot-image-left-panel",
                            Item: HotspotPanel,
                            cleanup: () => {
                                dispatch(closeHotspotPanel())
                            },
                            options: {},
                        })
                        registerTopBar({
                            Item: HotspotTopBar,
                            key: "hotspot-image-top-bar",
                        })
                    } else {
                        unregisterLeftPanel("hotspot-image-left-panel")
                        unregisterTopBar("hotspot-image-top-bar")
                    }
                })

                return <div></div>
            },
        }))
        .implement("feature-plugin", () => ({
            subscriptionType: AddOns.Hotspots,
            sampleDataModalName: ModalNames.TryHotspotsModal,
        }))
}
